import React, { useState } from "react";
import { generateId } from "../../utils/Utils";
import { TimePicker } from "../time-picker/TimePicker";
import { Timer, SubTimer } from "../../types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./AddTimerModal.css";

interface AddTimerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (timer: Timer) => void;
}

export const AddTimerModal: React.FC<AddTimerModalProps> = ({ isOpen, onClose, onAdd }) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [mode, setMode] = useState<'simple' | 'steps'>('simple');
    const [simpleDuration, setSimpleDuration] = useState(60);
    const [subtimers, setSubtimers] = useState<SubTimer[]>([{ id: generateId(), text: '', targetTime: 60, timeHistory: [], completed: false }]);

    const handleAddSubtimer = () => {
        setSubtimers([...subtimers, { id: generateId(), text: '', targetTime: 60, timeHistory: [], completed: false }]);
    };

    const handleRemoveSubtimer = (index: number) => {
        setSubtimers(subtimers.filter((_, i) => i !== index));
    };

    const handleSubtimerChange = (index: number, field: string, value: string | number) => {
        const updated = subtimers.map((s, i) => 
            i === index ? { ...s, [field]: value } : s
        );
        setSubtimers(updated);
    };

    const handleSubmit = () => {
        if (!title.trim()) return;
        
        const validSubtimers = subtimers.filter(s => s.text.trim());
        
        const newTimer: Timer = {
            id: generateId(),
            type: 'timer',
            title: title.trim(),
            mode,
            subtimers: mode === 'simple'
                ? [{ id: generateId(), text: title.trim(), targetTime: simpleDuration, timeHistory: [], completed: false }]
                : validSubtimers.length > 0 ? validSubtimers : [{ id: generateId(), text: t('addTimer.defaultStep'), targetTime: 60, timeHistory: [], completed: false }],
            completedDates: [],
        };

        onAdd(newTimer);
        setTitle('');
        setMode('simple');
        setSimpleDuration(60);
        setSubtimers([{ id: generateId(), text: '', targetTime: 60, timeHistory: [], completed: false }]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="add-timer-modal-overlay" onClick={onClose}>
            <div className="add-timer-modal" onClick={(e) => e.stopPropagation()}>
                <div className="add-timer-header">
                    <div>
                        <h3>{t('addTimer.addTimer')}</h3>
                        <p>{mode === 'simple' ? t('addTimer.simple') : `${t('addTimer.subtimers')} ${subtimers.length}`}</p>
                    </div>
                    <button
                        type="button"
                        className="add-timer-close"
                        onClick={onClose}
                        aria-label={t('addTimer.cancel')}
                        title={t('addTimer.cancel')}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="add-timer-field">
                    <label>{t('addTimer.timerName')}</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('addTimer.enterTimerName')}
                        className="add-timer-input"
                    />
                </div>
                <div className="element-mode-selector" role="group" aria-label={t('addTimer.mode')}>
                    <button type="button" className={mode === 'simple' ? 'active' : ''} onClick={() => setMode('simple')}>{t('addTimer.simple')}</button>
                    <button type="button" className={mode === 'steps' ? 'active' : ''} onClick={() => setMode('steps')}>{t('addTimer.withSteps')}</button>
                </div>
                {mode === 'simple' ? (
                    <div className="simple-duration-field">
                        <label>{t('addTimer.duration')}</label>
                        <TimePicker value={simpleDuration} onChange={setSimpleDuration} variant="popover" />
                    </div>
                ) : <div className="add-timer-subtimers">
                    <div className="add-timer-section-heading">
                        <label>{t('addTimer.subtimers')}</label>
                        <span>{subtimers.length}</span>
                    </div>
                    {subtimers.map((subtimer, index) => (
                        <div key={subtimer.id} className="add-timer-subtimer-item">
                            <span className="add-timer-subtimer-number">{index + 1}</span>
                            <input
                                type="text"
                                value={subtimer.text}
                                onChange={(e) => handleSubtimerChange(index, 'text', e.target.value)}
                                placeholder={t('addTimer.subtimerName')}
                                className="add-timer-subtimer-input"
                            />
                            <TimePicker
                                value={subtimer.targetTime}
                                onChange={(val: number) => handleSubtimerChange(index, 'targetTime', val)}
                                variant="popover"
                            />
                            {subtimers.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSubtimer(index)}
                                    className="add-timer-remove-button"
                                    aria-label={t('timerElement.removeSubtimer')}
                                    title={t('timerElement.removeSubtimer')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={handleAddSubtimer} className="add-timer-add-subtimer">
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('addTimer.addSubtimer')}</span>
                    </button>
                </div>}
                <div className="add-timer-actions">
                    <button onClick={onClose} className="add-timer-cancel">{t('addTimer.cancel')}</button>
                    <button onClick={handleSubmit} className="add-timer-submit">{t('addTimer.addTimerButton')}</button>
                </div>
            </div>
        </div>
    );
};
